import { isNil, noop, range } from "lodash";
import { forwardRef, useEffect, useRef, useState } from "react";
import useCssVariables from "shared/hooks/useCssVariables";
import { openPdf } from "./pdf-reader";

const NEAR_VIEW = "800px 0px";

const pageSizes = (pdf) =>
  Promise.all(
    range(1, pdf.numPages + 1).map((number) =>
      pdf.getPage(number).then((page) => {
        const { width, height } = page.getViewport({ scale: 1 });
        return { width, height };
      }),
    ),
  );

export const usePdfDocument = (bytes) => {
  const [state, setState] = useState({ pdf: null, sizes: [], failed: false });
  const currentRef = useRef(null);

  useEffect(
    () => () => {
      currentRef.current?.destroy();
      currentRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (isNil(bytes)) return undefined;
    let cancelled = false;
    let task = null;
    let adopted = false;
    openPdf(bytes)
      .then((loading) => {
        task = loading;
        if (cancelled) {
          loading.destroy();
          return null;
        }
        return loading.promise.then((pdf) =>
          pageSizes(pdf).then((sizes) => {
            if (cancelled) return;
            adopted = true;
            const previous = currentRef.current;
            currentRef.current = pdf;
            setState({ pdf, sizes, failed: false });
            if (!isNil(previous)) previous.destroy();
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setState((current) => ({ ...current, failed: true }));
      });
    return () => {
      cancelled = true;
      if (!adopted) task?.destroy();
    };
  }, [bytes]);

  return state;
};

const PdfPageView = (
  { pdf, scrollRoot, number, size, scale, children },
  ref,
) => {
  const holderRef = useRef(null);
  const canvasRef = useRef(null);
  const [seen, setSeen] = useState(false);
  useCssVariables(holderRef, {
    "--page-width": size.width * scale,
    "--page-height": size.height * scale,
  });

  useEffect(() => {
    const holder = holderRef.current;
    if (seen || isNil(holder) || isNil(window.IntersectionObserver)) {
      if (!seen) setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
      },
      { root: scrollRoot?.current ?? null, rootMargin: NEAR_VIEW },
    );
    observer.observe(holder);
    return () => observer.disconnect();
  }, [seen, scrollRoot]);

  useEffect(() => {
    if (!seen || isNil(pdf)) return undefined;
    let cancelled = false;
    let render = null;
    pdf
      .getPage(number)
      .then((page) => {
        if (cancelled) return null;
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * ratio });
        const buffer = document.createElement("canvas");
        buffer.width = Math.floor(viewport.width);
        buffer.height = Math.floor(viewport.height);
        render = page.render({
          canvasContext: buffer.getContext("2d"),
          viewport,
        });
        return render.promise.then(() => {
          const canvas = canvasRef.current;
          if (cancelled || isNil(canvas)) return;
          canvas.width = buffer.width;
          canvas.height = buffer.height;
          canvas.getContext("2d").drawImage(buffer, 0, 0);
        });
      })
      .catch(noop);
    return () => {
      cancelled = true;
      render?.cancel();
    };
  }, [seen, pdf, number, scale]);

  const setHolder = (node) => {
    holderRef.current = node;
    if (typeof ref === "function") ref(node);
  };

  return (
    <div ref={setHolder} className="dynamic-documents-editor__page">
      <canvas ref={canvasRef} className="dynamic-documents-editor__canvas" />
      {children}
    </div>
  );
};

export const PdfPage = forwardRef(PdfPageView);
